import kanbanService from 'dashboard/services/kanbanService';
import LabelsAPI from 'dashboard/api/labels';

const extractConversationPayload = response =>
  response?.data?.data?.payload || response?.data?.payload || [];

const moduleState = {
  conversationsByLabel: {},
  kanbanLabels: [],
  untaggedConversations: [],
  isLoading: false,
  error: null,
};

const getters = {
  getConversationsByLabel: currentState => labelId =>
    currentState.conversationsByLabel[labelId] || [],
  getKanbanLabels: currentState => currentState.kanbanLabels,
  getUntaggedConversations: currentState => currentState.untaggedConversations,
  isLoading: currentState => currentState.isLoading,
  getError: currentState => currentState.error,
  getAllConversationsByLabel: currentState => currentState.conversationsByLabel,
};

const findConversationAcrossColumns = (currentState, conversationId) => {
  const untaggedConversation = currentState.untaggedConversations.find(
    conversation => conversation.id === conversationId
  );

  if (untaggedConversation) {
    return untaggedConversation;
  }

  return Object.values(currentState.conversationsByLabel)
    .flat()
    .find(conversation => conversation.id === conversationId);
};

const actions = {
  async loadKanbanData({ commit, dispatch }, filters = {}) {
    commit('setLoading', true);

    try {
      const labelsResponse = await LabelsAPI.get();
      const labels = labelsResponse.data.payload || [];
      const kanbanLabels = labels.filter(label => label.show_on_sidebar);

      commit('setKanbanLabels', kanbanLabels);
      await dispatch('loadConversationsForBoard', { kanbanLabels, filters });
      commit('setError', null);
    } catch (error) {
      commit('setError', error.message);
    } finally {
      commit('setLoading', false);
    }
  },

  async loadConversationsForBoard({ commit }, { kanbanLabels, filters = {} }) {
    const response = await kanbanService.getConversations({
      status: 'open',
      ...filters,
    });

    const conversations = extractConversationPayload(response);
    const kanbanLabelTitleById = new Map(
      kanbanLabels.map(label => [label.id, label.title])
    );
    const conversationsByLabel = Object.fromEntries(
      kanbanLabels.map(label => [label.id, []])
    );
    const untaggedConversations = [];

    conversations.forEach(conversation => {
      const labels = conversation.labels || [];
      let hasKanbanLabel = false;

      kanbanLabelTitleById.forEach((labelTitle, labelId) => {
        if (labels.includes(labelTitle)) {
          hasKanbanLabel = true;
          conversationsByLabel[labelId].push(conversation);
        }
      });

      if (!hasKanbanLabel) {
        untaggedConversations.push(conversation);
      }
    });

    commit('setConversationsByLabelMap', conversationsByLabel);
    commit('setUntaggedConversations', untaggedConversations);
  },

  async moveConversationToLabel(
    { commit, state: currentState },
    { conversationId, fromLabelId, labelId, labelTitle, toIndex }
  ) {
    commit('setLoading', true);

    try {
      const conversation = findConversationAcrossColumns(
        currentState,
        conversationId
      ) || {
        id: conversationId,
        labels: [],
      };

      if (labelId === fromLabelId) {
        commit('moveConversation', {
          conversation,
          fromLabelId,
          toLabelId: labelId,
          toIndex,
        });
        commit('setError', null);
        return;
      }

      const kanbanLabelTitles = currentState.kanbanLabels.map(
        label => label.title
      );
      const preservedLabels = (conversation.labels || []).filter(
        label => !kanbanLabelTitles.includes(label)
      );
      const nextLabels =
        labelId === 'untagged' || !labelTitle
          ? preservedLabels
          : [...new Set([...preservedLabels, labelTitle])];

      await kanbanService.updateConversationLabels(conversationId, nextLabels);

      commit('moveConversation', {
        conversation: { ...conversation, labels: nextLabels },
        fromLabelId,
        toLabelId: labelId,
        toIndex,
      });
      commit('setError', null);
    } catch (error) {
      commit('setError', error.message);
    } finally {
      commit('setLoading', false);
    }
  },

  async removeConversationFromLabel(
    { commit, state: currentState },
    { conversationId, labelId }
  ) {
    try {
      await kanbanService.updateConversationLabels(conversationId, []);

      const conversation =
        currentState.conversationsByLabel[labelId]?.find(
          item => item.id === conversationId
        ) || null;

      commit('removeConversationFromColumn', { labelId, conversationId });
      if (conversation) {
        commit('appendUntaggedConversation', conversation);
      }
      commit('setError', null);
    } catch (error) {
      commit('setError', error.message);
    }
  },

  async refreshKanbanData({ dispatch }, filters = {}) {
    await dispatch('loadKanbanData', filters);
  },
};

const mutations = {
  setLoading(currentState, value) {
    currentState.isLoading = value;
  },

  setError(currentState, error) {
    currentState.error = error;
  },

  setKanbanLabels(currentState, labels) {
    currentState.kanbanLabels = labels;
  },

  setConversationsByLabelMap(currentState, conversationsByLabel) {
    currentState.conversationsByLabel = conversationsByLabel;
  },

  setUntaggedConversations(currentState, conversations) {
    currentState.untaggedConversations = conversations;
  },

  removeConversationFromColumn(currentState, { labelId, conversationId }) {
    if (labelId === 'untagged') {
      currentState.untaggedConversations =
        currentState.untaggedConversations.filter(
          conversation => conversation.id !== conversationId
        );
      return;
    }

    currentState.conversationsByLabel = {
      ...currentState.conversationsByLabel,
      [labelId]: (currentState.conversationsByLabel[labelId] || []).filter(
        conversation => conversation.id !== conversationId
      ),
    };
  },

  appendUntaggedConversation(currentState, conversation) {
    const hasConversation = currentState.untaggedConversations.some(
      item => item.id === conversation.id
    );

    if (!hasConversation) {
      currentState.untaggedConversations = [
        ...currentState.untaggedConversations,
        conversation,
      ];
    }
  },

  moveConversation(currentState, { conversation, toLabelId, toIndex }) {
    const removeConversation = conversations =>
      conversations.filter(item => item.id !== conversation.id);

    const nextUntaggedConversations = removeConversation(
      currentState.untaggedConversations
    );

    const nextConversationsByLabel = Object.fromEntries(
      Object.entries(currentState.conversationsByLabel).map(
        ([labelKey, conversations]) => [
          labelKey,
          removeConversation(conversations || []),
        ]
      )
    );

    if (toLabelId === 'untagged') {
      const insertionIndex = Number.isInteger(toIndex)
        ? Math.max(0, Math.min(toIndex, nextUntaggedConversations.length))
        : nextUntaggedConversations.length;

      nextUntaggedConversations.splice(insertionIndex, 0, conversation);
    } else {
      const targetConversations = nextConversationsByLabel[toLabelId] || [];
      const insertionIndex = Number.isInteger(toIndex)
        ? Math.max(0, Math.min(toIndex, targetConversations.length))
        : targetConversations.length;

      targetConversations.splice(insertionIndex, 0, conversation);
      nextConversationsByLabel[toLabelId] = targetConversations;
    }

    currentState.untaggedConversations = nextUntaggedConversations;
    currentState.conversationsByLabel = nextConversationsByLabel;
  },
};

export default {
  namespaced: true,
  state: moduleState,
  getters,
  actions,
  mutations,
};
